import { createContext, useContext, useMemo } from 'react';
import { ANYWHERE_API_PATH, normalizeAnywhereBasePath } from './client';
import { createAnywhereService } from './anywhereService';

/**
 * Scope is deliberately request-local.  An administrator can have two Anywhere
 * views open at once, so changing a module-wide base path would be unsafe.
 */
const AnywhereScopeContext = createContext({
  basePath: ANYWHERE_API_PATH,
  scopeKey: 'client',
  idClient: null,
  isAdmin: false,
  clients: [],
  service: createAnywhereService(ANYWHERE_API_PATH),
});

export const parseAnywhereClientId = (value) => {
  const rawValue = String(value ?? '');
  if (!/^[1-9]\d*$/.test(rawValue)) return null;

  const idClient = Number(rawValue);
  return Number.isSafeInteger(idClient) ? idClient : null;
};

export const makeAnywhereAdminBasePath = (idClient) => {
  const numericId = parseAnywhereClientId(idClient);
  if (numericId == null) {
    throw new Error('idClient deve ser um inteiro positivo.');
  }
  return `${ANYWHERE_API_PATH}/admin/clientes/${numericId}`;
};

export const anywherePath = (basePath, path = '') => {
  const normalizedBase = normalizeAnywhereBasePath(basePath);
  const normalizedPath = String(path).replace(/^\/+/, '');
  return normalizedPath ? `${normalizedBase}/${normalizedPath}` : normalizedBase;
};

/**
 * Build a browser route for a screen in the current scope.  The service base path and the browser
 * route deliberately share the same shape, but keeping this helper separate prevents a screen from
 * accidentally linking an administrative view back into the tenant-only `/anywhere/*` tree.
 */
export const makeAnywhereScreenBasePath = ({ isAdmin = false, idClient = null } = {}) => {
  const numericId = parseAnywhereClientId(idClient);
  return isAdmin && numericId != null ? `/anywhere/admin/clientes/${numericId}` : ANYWHERE_API_PATH;
};

export const anywhereScreenPath = (scope, path = '') => anywherePath(makeAnywhereScreenBasePath(scope), path);

/** Stable path builder for links between the shared phase-1 screens. */
export const useAnywhereScreenPath = () => {
  const { isAdmin, idClient } = useAnywhereScope();
  return useMemo(
    () =>
      (path = '') =>
        anywhereScreenPath({ isAdmin, idClient }, path),
    [isAdmin, idClient]
  );
};

export function AnywhereScopeProvider({ basePath = ANYWHERE_API_PATH, idClient = null, clients = [], children }) {
  const normalizedBasePath = normalizeAnywhereBasePath(basePath);
  const value = useMemo(() => {
    const isAdmin = normalizedBasePath !== ANYWHERE_API_PATH;
    return {
      basePath: normalizedBasePath,
      // Including the complete base path in query keys isolates admin client X
      // from client Y and from the regular tenant view.
      scopeKey: normalizedBasePath,
      idClient: idClient == null ? null : Number(idClient),
      isAdmin,
      clients: Array.isArray(clients) ? clients : [],
      service: createAnywhereService(normalizedBasePath),
    };
  }, [clients, idClient, normalizedBasePath]);

  return <AnywhereScopeContext.Provider value={value}>{children}</AnywhereScopeContext.Provider>;
}

export const useAnywhereScope = () => useContext(AnywhereScopeContext);
